import { useState, useCallback } from 'react';
import { useApp } from '../../../context/AppContext';
import LiceosAPI from '../../../api/liceos';
import logger from '../../../utils/logger';

/**
 * Hook personalizado para manejar liceos
 */
export const useLiceos = () => {
    const { notify } = useApp();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    const handleError = useCallback((err, defaultMessage = 'Error en la operación') => {
        const errorMessage = err?.response?.data?.message || err?.message || defaultMessage;
        logger.error('Error en Liceos API:', err);
        setError(errorMessage);
        notify({
            type: 'error',
            title: 'Error',
            text: errorMessage
        });
    }, [notify]);

    const handleSuccess = useCallback((message = 'Operación exitosa') => {
        setError(null);
        notify({
            type: 'success',
            title: 'Éxito',
            text: message
        });
    }, [notify]);

    // ==================== LICEOS ====================

    const getLiceos = useCallback(async (filters = {}) => {
        setIsLoading(true);
        setError(null);
        try {
            const data = await LiceosAPI.getAll(filters);
            // Manejar diferentes formatos de respuesta
            let liceos = [];
            if (data.liceos && Array.isArray(data.liceos)) {
                liceos = data.liceos;
            } else if (Array.isArray(data)) {
                liceos = data;
            }
            return liceos;
        } catch (err) {
            handleError(err, 'Error al obtener liceos');
            return [];
        } finally {
            setIsLoading(false);
        }
    }, [handleError]);

    const getLiceoById = useCallback(async (id) => {
        setIsLoading(true);
        setError(null);
        try {
            const data = await LiceosAPI.getById(id);
            return data;
        } catch (err) {
            handleError(err, 'Error al obtener liceo');
            return null;
        } finally {
            setIsLoading(false);
        }
    }, [handleError]);

    const createLiceo = useCallback(async (data) => {
        setIsLoading(true);
        setError(null);
        try {
            const result = await LiceosAPI.create(data);
            handleSuccess('Liceo creado exitosamente');
            return result;
        } catch (err) {
            handleError(err, 'Error al crear liceo');
            return null;
        } finally {
            setIsLoading(false);
        }
    }, [handleError, handleSuccess]);

    const updateLiceo = useCallback(async (id, data) => {
        setIsLoading(true);
        setError(null);
        try {
            const result = await LiceosAPI.update(id, data);
            handleSuccess('Liceo actualizado exitosamente');
            return result;
        } catch (err) {
            handleError(err, 'Error al actualizar liceo');
            return null;
        } finally {
            setIsLoading(false);
        }
    }, [handleError, handleSuccess]);

    const deleteLiceo = useCallback(async (id) => {
        setIsLoading(true);
        setError(null);
        try {
            await LiceosAPI.delete(id);
            handleSuccess('Liceo eliminado exitosamente');
            return true;
        } catch (err) {
            handleError(err, 'Error al eliminar liceo');
            return false;
        } finally {
            setIsLoading(false);
        }
    }, [handleError, handleSuccess]);

    const getLiceoStats = useCallback(async (id) => {
        setIsLoading(true);
        setError(null);
        try {
            const data = await LiceosAPI.getStats(id);
            return data;
        } catch (err) {
            handleError(err, 'Error al obtener estadísticas del liceo');
            return null;
        } finally {
            setIsLoading(false);
        }
    }, [handleError]);

    const getLiceoRanking = useCallback(async (id, filters = {}) => {
        setIsLoading(true);
        setError(null);
        try {
            const data = await LiceosAPI.getRankingById(id, filters);
            return data;
        } catch (err) {
            handleError(err, 'Error al obtener ranking del liceo');
            return null;
        } finally {
            setIsLoading(false);
        }
    }, [handleError]);

    // ==================== USUARIOS DE LICEOS ====================

    const getUsers = useCallback(async (filters = {}) => {
        setIsLoading(true);
        setError(null);
        try {
            const data = await LiceosAPI.getUsers(filters);
            let users = [];
            if (data.users && Array.isArray(data.users)) {
                users = data.users;
            } else if (Array.isArray(data)) {
                users = data;
            }
            return users;
        } catch (err) {
            handleError(err, 'Error al obtener usuarios');
            return [];
        } finally {
            setIsLoading(false);
        }
    }, [handleError]);

    const getUserById = useCallback(async (id) => {
        setIsLoading(true);
        setError(null);
        try {
            const data = await LiceosAPI.getUserById(id);
            return data;
        } catch (err) {
            handleError(err, 'Error al obtener usuario');
            return null;
        } finally {
            setIsLoading(false);
        }
    }, [handleError]);

    const createUser = useCallback(async (data) => {
        setIsLoading(true);
        setError(null);
        try {
            const result = await LiceosAPI.createUser(data);
            handleSuccess('Usuario creado exitosamente');
            return result;
        } catch (err) {
            handleError(err, 'Error al crear usuario');
            return null;
        } finally {
            setIsLoading(false);
        }
    }, [handleError, handleSuccess]);

    const createUserComplete = useCallback(async (data) => {
        setIsLoading(true);
        setError(null);
        try {
            const result = await LiceosAPI.createUserComplete(data);
            handleSuccess('Usuario creado exitosamente en todos los sistemas');
            return result;
        } catch (err) {
            handleError(err, 'Error al crear usuario completo');
            return null;
        } finally {
            setIsLoading(false);
        }
    }, [handleError, handleSuccess]);

    const updateUser = useCallback(async (id, data) => {
        setIsLoading(true);
        setError(null);
        try {
            const result = await LiceosAPI.updateUser(id, data);
            handleSuccess('Usuario actualizado exitosamente');
            return result;
        } catch (err) {
            handleError(err, 'Error al actualizar usuario');
            return null;
        } finally {
            setIsLoading(false);
        }
    }, [handleError, handleSuccess]);

    const deleteUser = useCallback(async (id) => {
        setIsLoading(true);
        setError(null);
        try {
            await LiceosAPI.deleteUser(id);
            handleSuccess('Usuario eliminado exitosamente');
            return true;
        } catch (err) {
            handleError(err, 'Error al eliminar usuario');
            return false;
        } finally {
            setIsLoading(false);
        }
    }, [handleError, handleSuccess]);

    // ==================== USUARIOS DE LICEOS ====================

    const getLiceoUsers = useCallback(async (liceoId, filters = {}) => {
        setIsLoading(true);
        setError(null);
        try {
            const data = await LiceosAPI.getLiceoUsers(liceoId, filters);
            // Manejar diferentes formatos de respuesta
            let users = [];
            if (data.users && Array.isArray(data.users)) {
                users = data.users;
            } else if (Array.isArray(data)) {
                users = data;
            }
            return users;
        } catch (err) {
            handleError(err, 'Error al obtener usuarios del liceo');
            return [];
        } finally {
            setIsLoading(false);
        }
    }, [handleError]);

    const addUserToLiceo = useCallback(async (liceoId, userId) => {
        setIsLoading(true);
        setError(null);
        try {
            const result = await LiceosAPI.addUser(liceoId, userId);
            handleSuccess('Usuario agregado al liceo exitosamente');
            return result;
        } catch (err) {
            handleError(err, 'Error al agregar usuario al liceo');
            return null;
        } finally {
            setIsLoading(false);
        }
    }, [handleError, handleSuccess]);

    const removeUserFromLiceo = useCallback(async (liceoId, userId) => {
        setIsLoading(true);
        setError(null);
        try {
            await LiceosAPI.removeUser(liceoId, userId);
            handleSuccess('Usuario removido del liceo exitosamente');
            return true;
        } catch (err) {
            handleError(err, 'Error al remover usuario del liceo');
            return false;
        } finally {
            setIsLoading(false);
        }
    }, [handleError, handleSuccess]);

    const getUserTypes = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const data = await LiceosAPI.getUserTypes();
            return Array.isArray(data) ? data : [];
        } catch (err) {
            handleError(err, 'Error al obtener tipos de usuario');
            return [];
        } finally {
            setIsLoading(false);
        }
    }, [handleError]);

    // ==================== ESTADÍSTICAS ====================

    const getStatisticsOverview = useCallback(async (filters = {}) => {
        setIsLoading(true);
        setError(null);
        try {
            const data = await LiceosAPI.getStatisticsOverview(filters);
            return data;
        } catch (err) {
            handleError(err, 'Error al obtener estadísticas');
            return null;
        } finally {
            setIsLoading(false);
        }
    }, [handleError]);

    const getKPIs = useCallback(async (filters = {}) => {
        setIsLoading(true);
        setError(null);
        try {
            const data = await LiceosAPI.getKPIs(filters);
            return data;
        } catch (err) {
            handleError(err, 'Error al obtener KPIs');
            return null;
        } finally {
            setIsLoading(false);
        }
    }, [handleError]);

    // ==================== DETALLE DE USUARIO ====================

    const getUserDetail = useCallback(async (userId) => {
        setIsLoading(true);
        setError(null);
        try {
            const data = await LiceosAPI.getUserDetail(userId);
            return data;
        } catch (err) {
            handleError(err, 'Error al obtener detalle del usuario');
            return null;
        } finally {
            setIsLoading(false);
        }
    }, [handleError]);

    const getUserPoints = useCallback(async (userId) => {
        setIsLoading(true);
        setError(null);
        try {
            const data = await LiceosAPI.getUserPoints(userId);
            return data;
        } catch (err) {
            handleError(err, 'Error al obtener puntos del usuario');
            return null;
        } finally {
            setIsLoading(false);
        }
    }, [handleError]);

    const getUserLearnwordsCourses = useCallback(async (userId) => {
        setIsLoading(true);
        setError(null);
        try {
            const data = await LiceosAPI.getUserLearnwordsCourses(userId);
            return data;
        } catch (err) {
            handleError(err, 'Error al obtener cursos de learnwords');
            return null;
        } finally {
            setIsLoading(false);
        }
    }, [handleError]);

    return {
        // Estado
        isLoading,
        error,

        // Liceos
        getLiceos,
        getLiceoById,
        createLiceo,
        updateLiceo,
        deleteLiceo,
        getLiceoStats,
        getLiceoRanking,

        // Usuarios
        getUsers,
        getUserById,
        createUser,
        createUserComplete,
        updateUser,
        deleteUser,
        getUserTypes,

        // Usuarios de Liceos
        getLiceoUsers,
        addUserToLiceo,
        removeUserFromLiceo,

        // Estadísticas
        getStatisticsOverview,
        getKPIs,

        // Detalle de Usuario
        getUserDetail,
        getUserPoints,
        getUserLearnwordsCourses,

        // API directa
        api: LiceosAPI
    };
};

